import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withRouting from '@om-fe/core/core/routing/with-routing';
import { EMRoutingContextInterface } from '@om-fe/core/core/routing/utils';
import { DisciplineLiveStatus } from '@om-fe/models';
import { DEFAULT_DISPLAYED_NUMBER_OF_MATCHES } from '../../match-list/utils/match-list.constants';
import { isBrowser } from '@om-fe/utils';
import {
    getTopBetsTimeRangeSliceName,
    TopBetsStorageData,
    TopBetsRouteParams,
} from '../utils/top-bets.utils';
import getSlice from '@om-fe/core/utils/get-slice';
import TimeRangeNav from '../containers/time-range-nav/time-range-nav';
import TopBetsMatchListSelector from '../containers/top-bets-list/top-bets-list-selector';
import LiveMatchListSelector from '../../match-list/containers/live-match-list/live-match-list-selector';
import { getTopBetsTimeRange, removeTopBetsTimeRange } from '../containers/time-range-nav/time-range-nav.actions';
import { navBem } from '@om-fe/widgets/dist/components/nav/nav.utils';
import getConfig, { OMFE2ConfigModel, BannerMatrixConfig } from '@om-fe/core/utils/config';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { TimeRangeModel } from '../utils/top-bets.model';
import { TimeRangeDisplay, TimeRangeType } from '../utils/top-bets.constants';
import { TimeRangeListItemModel, TimeRangeListStateModel } from '../containers/time-range-nav/time-range-nav.reducer';
import { useParams } from 'react-router';
import { getVirtualStateExtraParams, setVirtualStateExtraParams, setVirtualStateAction } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';

export interface TopBetsContentPublicProps {
    readonly instanceId: string;
    readonly breakpoints: Breakpoints;
    readonly disciplineId: string;
    readonly locationId?: string;
    readonly categoryId?: string;
    readonly isCategory?: boolean;
    readonly isTopBets?: boolean;
    readonly mainMarketsLimit?: number;
    readonly onContentReady?: () => void;
    readonly forceTodayTimeRange?: boolean;
    readonly onForceTodayTimeRangeHandled?: (disciplineId: string) => void;
}
interface TopBetsStoreProps {
    readonly list: string[];
    readonly ready: boolean;
    readonly items: Record<string, TimeRangeListItemModel>;
    readonly isAuthenForTopic?: boolean;
    readonly sliceDisciplineId: string;
    readonly sliceLocationId?: string;
    readonly sliceCategoryId?: string;
}

type TopBetsMatchListProps = TopBetsContentPublicProps
    & TopBetsStoreProps
    & I18nContextInterface
    & EMRoutingContextInterface;

const config: OMFE2ConfigModel = getConfig();
const bannerMatrixConfig: BannerMatrixConfig | undefined = config.bannerMatrix;
const enabledTopicDelay: boolean | undefined = config.enabledTopicDelay;
const defaultTimeRange: string | undefined = config.defaultTimeRange;

const getDefaultTimeRangeValue = (list: string[], item: TimeRangeModel[], forceItem?: boolean) => {
    if (!list.length ) {
        return undefined;
    } else {
        const findItem = Object.values(item).filter((key) => {
            return (defaultTimeRange === TimeRangeDisplay.Live
                ? key.timeRangeType === TimeRangeType.LIVE
                : (key.timeRangeType === TimeRangeType.DAYS && key.value.toString() === '0')
            );
        });
        const listId: string = findItem.length ? findItem[0].id : list[0];
        const items: TimeRangeModel = forceItem ? item : item[listId];

        if (items.timeRangeType === TimeRangeType.HOURS) {
            return `0-${items.value}`;
        } else if(items.timeRangeType === TimeRangeType.DAYS && items.value.toString() === '0') {
            const start = new Date();
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        } else if (items.timeRangeType === TimeRangeType.LATER) {
            const start = new Date();
            start.setDate(start.getDate() + (items.value * 1));
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setDate(end.getDate() + (items.value * 1));
            end.setFullYear(end.getFullYear() + 1);
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        } else {
            const start = new Date();
            start.setDate(start.getDate() + (items.value * 1));
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setDate(end.getDate() + (items.value * 1));
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        }
    }
};

const getTimeRangeValue = (item: TimeRangeModel) => {
    if (item) {
        if (item.timeRangeType === TimeRangeType.HOURS) {
            return `0-${item.value}`;
        } else if(item.timeRangeType === TimeRangeType.DAYS && item.value.toString() === '0') {
            const start = new Date();
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        } else if (item.timeRangeType === TimeRangeType.LATER) {
            const start = new Date();
            start.setDate(start.getDate() + (item.value * 1));
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setDate(end.getDate() + (item.value * 1));
            end.setFullYear(end.getFullYear() + 1);
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        } else {
            const start = new Date();
            start.setDate(start.getDate() + (item.value * 1));
            start.setHours(0,0,0,0);
            const epochStart = Math.floor(start.getTime() / 1000.0);
            const end = new Date();
            end.setDate(end.getDate() + (item.value * 1));
            end.setHours(23,59,59,999);
            const epochEnd = Math.floor(end.getTime() / 1000.0);

            return `${epochStart}-${epochEnd}`;
        }
    }

    return undefined;
};

const getDefaultActiveTab = (list: string[], item: Record<string, TimeRangeModel>) => {
    if (!list.length) {
        return '';
    }

    const findItem = Object.values(item).filter((key) => {
        return (defaultTimeRange === TimeRangeDisplay.Live
            ? key.timeRangeType === TimeRangeType.LIVE
            : (key.timeRangeType === TimeRangeType.DAYS && key.value.toString() === '0')
        );
    });

    if (findItem.length) {
        return findItem[0].id;
    }

    return list[0];
}

const getDefaultActiveItem = (list: string[], item: Record<string, TimeRangeModel>) => {
    if (!list.length) {
        return undefined;
    }

    const findItem = Object.values(item).filter((key) => {
        return (defaultTimeRange === TimeRangeDisplay.Live
            ? key.timeRangeType === TimeRangeType.LIVE
            : (key.timeRangeType === TimeRangeType.DAYS && key.value.toString() === '0')
        );
    });

    if (findItem.length) {
        return item[findItem[0].id];
    }

    return item[list[0]];
}

function TopBetsContentComponent (
    {
        instanceId,
        disciplineId,
        isCategory,
        deviceType,
        isTopBets,
        list,
        locationId,
        categoryId,
        items,
        mainMarketsLimit,
        isAuthenForTopic,
        ready,
        onContentReady,
        forceTodayTimeRange,
        onForceTodayTimeRangeHandled,
        sliceDisciplineId,
        sliceLocationId,
        sliceCategoryId,
    }: TopBetsMatchListProps,
) {
    const { timeRoute } = useParams<TopBetsRouteParams>();
    const { gettext } = useI18NContext();
    const [timeRange, setTimeRange] = useState<undefined | string>(getDefaultTimeRangeValue(list, items));
    const [activeTimeRangeItem, setActiveTimeRangeItem] = useState<TimeRangeModel | undefined>(getDefaultActiveItem(list, items));
    const [activeTimeRangeTab, setActiveTimeRangeTab] = useState<string>(getDefaultActiveTab(list, items));

    useEffect(() => {
        if (ready && list.length > 0 && onContentReady) {
            onContentReady();
        }
    }, [ready, list.length, onContentReady]);

    useEffect(() => {
        if(forceTodayTimeRange) return;

        if (timeRoute) {
            const timeRouteId: boolean = list.includes(timeRoute);
            if (!timeRouteId) {
                setActiveTimeRangeTab(activeTimeRangeTab);
                setActiveTimeRangeItem(items[activeTimeRangeTab]);
                setTimeRange(getDefaultTimeRangeValue(list, items[activeTimeRangeTab], true));
                setVirtualStateExtraParams({ sportId: disciplineId, timeRangeId: activeTimeRangeTab, matchLimit: 0 });
            } else {
                setActiveTimeRangeTab(timeRoute);
                setActiveTimeRangeItem(items[timeRoute]);
                setTimeRange(getDefaultTimeRangeValue(list, items[timeRoute], true));
                setVirtualStateExtraParams({ sportId: disciplineId, timeRangeId: timeRoute, matchLimit: 0 });
            }
        } else {
            const storage: TopBetsStorageData = getVirtualStateExtraParams<TopBetsStorageData>();
            const timeRangeStorage: boolean = list.includes(storage?.timeRangeId);
            setActiveTimeRangeTab(timeRangeStorage ? storage?.timeRangeId! : activeTimeRangeTab);
            setActiveTimeRangeItem(timeRangeStorage ? items[storage?.timeRangeId!] : items[activeTimeRangeTab]);
            setTimeRange(getDefaultTimeRangeValue(list, timeRangeStorage ? items[storage?.timeRangeId!] : items[activeTimeRangeTab], true));
            setVirtualStateExtraParams({ sportId: storage?.sportId ? storage.sportId : disciplineId, timeRangeId: timeRangeStorage ? storage?.timeRangeId! : activeTimeRangeTab });
        }
    }, [forceTodayTimeRange]);

    useEffect(() => {
        if (!forceTodayTimeRange || !ready || !list.length) {
            return;
        }

        const sliceMatchesProps: boolean = sliceDisciplineId === disciplineId
            && sliceLocationId === locationId
            && sliceCategoryId === categoryId;

        if (!sliceMatchesProps) {
            return;
        }

        const todayItemId: string | undefined = list.find((id) => {
            const item = items[id];

            return item?.timeRangeType === TimeRangeType.DAYS && item.value.toString() === '0';
        });
        const todayItem: TimeRangeModel | undefined = todayItemId ? items[todayItemId] : undefined;

        if (todayItem?.id && list.includes(todayItem.id)) {
            setActiveTimeRangeTab(todayItem.id);
            setActiveTimeRangeItem(todayItem);
            setTimeRange(getTimeRangeValue(todayItem));
            setVirtualStateExtraParams({ sportId: disciplineId, timeRangeId: todayItem.id, matchLimit: 0 });
            onForceTodayTimeRangeHandled?.();
        }

    }, [forceTodayTimeRange, ready, list, items, disciplineId, locationId, categoryId, sliceDisciplineId, sliceLocationId, sliceCategoryId, onForceTodayTimeRangeHandled]);

    const matchListLayout = config.matchListConfig?.commonConfig?.matchListItemLayout;
    const eventsNumber: number = config.matchListConfig?.commonConfig?.eventsNumber ?? DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;
    const marketSelector: boolean = config?.matchListConfig?.matchListSelector?.enabled ?? false;
    const marketGroupsOverviewNumber: number | undefined = config?.matchListConfig?.matchListSelector?.numberOfList;
    const showMarketSelectorHeader: boolean = config.matchListConfig?.matchListSelector?.showHeader ?? true;

    const handleTimeRangeFilter = useCallback((id?: string) => {
        if (isBrowser() && bannerMatrixConfig?.showOnFooter) {
            document.querySelector(`#BannerFooter`)?.classList.add('BannerWidgetBannerHidden');
        }

        if (id) {
            setVirtualStateAction();
            setVirtualStateExtraParams({ timeRangeId: id, matchLimit: 0 });
            setActiveTimeRangeItem(items[id]);
            setActiveTimeRangeTab(id);
            setTimeRange(getTimeRangeValue(items[id]));
        }
    }, [list]);

    const renderContent = useMemo(() => {
            if (activeTimeRangeItem?.timeRangeType === TimeRangeType.LIVE) {
                return (
                    <LiveMatchListSelector
                        itemLayout={matchListLayout}
                        disciplineId={disciplineId}
                        instanceId={`${instanceId}-Live-Match-List-${activeTimeRangeTab}`}
                        limitNumberOfEvents={eventsNumber}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        mainMarketsLimit={mainMarketsLimit}
                        marketGroupsOverviewNumber={marketGroupsOverviewNumber}
                        hasMarketSelector={marketSelector}
                        showMatchListHeader={false}
                        showMarketInMatchListGroupHeader={true}
                        deviceType={deviceType}
                        showMarketSelectorHeader={showMarketSelectorHeader}
                        locationId={isCategory ? undefined : locationId}
                        eventCategoryId={isCategory ? categoryId : undefined}
                        isAuthenForTopic={isAuthenForTopic}
                    />
                );
            } else {
                return (
                    <TopBetsMatchListSelector
                        itemLayout={matchListLayout}
                        disciplineId={disciplineId}
                        instanceId={`${instanceId}-Match-List-${activeTimeRangeTab}`}
                        limitNumberOfEvents={eventsNumber}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        mainMarketsLimit={mainMarketsLimit}
                        marketGroupsOverviewNumber={marketGroupsOverviewNumber}
                        hasMarketSelector={marketSelector}
                        showMatchListHeader={false}
                        showMarketInMatchListGroupHeader={true}
                        deviceType={deviceType}
                        showMarketSelectorHeader={showMarketSelectorHeader}
                        timeRangeType={activeTimeRangeItem?.timeRangeType}
                        timeRangeValue={activeTimeRangeItem?.value}
                        timeRange={timeRange}
                        isTopBets={isTopBets}
                        locationId={isCategory ? undefined : locationId}
                        categoryId={isCategory ? categoryId : undefined}
                        isCategory={isCategory}
                        isAuthenForTopic={isAuthenForTopic}
                    />
                );
            }
    }, [activeTimeRangeTab, isTopBets, locationId, categoryId, timeRange, mainMarketsLimit]);

    if (!list.length) {
        return (
            <div className="EmptyList">
                {gettext('No matches currently available')}
            </div>
        );
    }

    return (
        <>
            <div className={navBem()}>
                <TimeRangeNav
                    disciplineId={disciplineId}
                    locationId={locationId}
                    instanceId={instanceId}
                    activeTab={activeTimeRangeTab}
                    categoryId={categoryId}
                    preventRedirect={true}
                    noRedirectFilter={handleTimeRangeFilter}
                    list={list}
                    items={items}
                    ready={ready}
                />
            </div>
            { activeTimeRangeTab && renderContent }
        </>
    );
}

const TopBetsContent: React.ComponentType<TopBetsContentPublicProps> = compose<React.ComponentType<TopBetsContentPublicProps>>(
    withRouting(),
    withTranslation<TopBetsContentPublicProps>(),
    preLoader<TopBetsContentPublicProps>({
        preload({ props, dispatch }): void {
            const { disciplineId, instanceId, locationId, categoryId } = props;

            dispatch(getTopBetsTimeRange(instanceId, disciplineId, locationId, categoryId));
        },
        getReadySlice({ instanceId }: TopBetsContentPublicProps): string {
            const sliceName: string = getTopBetsTimeRangeSliceName(instanceId);

            return `${sliceName}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            if (
                props.disciplineId !== prevProps.disciplineId || props.instanceId !== prevProps.instanceId
                || props.locationId !== prevProps.locationId || props.categoryId !== prevProps.categoryId
            ) {
                dispatch(removeTopBetsTimeRange(
                    prevProps.instanceId,
                ));
                dispatch(getTopBetsTimeRange(
                    props.instanceId,
                    props.disciplineId,
                    props.locationId,
                    props.categoryId,
                ));
            }
        },
        destroy: ({ props, dispatch }) => {
            const { instanceId } = props;

            dispatch(removeTopBetsTimeRange(instanceId));
        },
    }),
    connect<TopBetsStoreProps, {} ,TopBetsContentPublicProps>(
        (state: AppState, props: TopBetsContentPublicProps): TopBetsStoreProps => {
            const { instanceId } = props
            const sliceName: string = getTopBetsTimeRangeSliceName(instanceId);
            const slice: TimeRangeListStateModel | undefined = getSlice<TimeRangeListStateModel>(state, sliceName);
            const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

            if (!slice) {
                return {
                    ready: false,
                    list: [],
                    items: {},
                    isAuthenForTopic,
                    sliceDisciplineId: '',
                    sliceLocationId: undefined,
                    sliceCategoryId: undefined,
                };
            }

            return {
                list: slice.list,
                items: slice.items,
                ready: slice.ready,
                isAuthenForTopic,
                sliceDisciplineId: slice.disciplineId,
                sliceLocationId: slice.locationId,
                sliceCategoryId: slice.categoryId,
            };
        },
    ),
)(TopBetsContentComponent);
export default TopBetsContent;
